import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  computed,
  inject,
} from '@angular/core';
import { LayoutService } from './service/app.layout.service';
import { AppMenuitemComponent } from './app.menuitem.component';
import { MemoryDisplayComponent } from '../shared/components/memory-display/memory-display.component';
import { AdminProfileStore } from '../store/admin-profile/admin-profile.store';
import { InquiriesStore } from '../store/inquiries/inquiries.store';
import { ActiveFestivalComponent } from '../shared/components/active-festival/active-festival.component';

@Component({
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
  imports: [
    AppMenuitemComponent,
    MemoryDisplayComponent,
    ActiveFestivalComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppMenuComponent {
  layoutService = inject(LayoutService);
  private adminProfileStore = inject(AdminProfileStore);
  private inquiriesStore = inject(InquiriesStore);

  isSuperAdmin: Signal<boolean> = this.adminProfileStore.isSuperAdmin;
  canWrite: Signal<boolean> = this.adminProfileStore.canWrite;

  constructor() {
    this.inquiriesStore.fetchAllInquiries();
  }

  model = computed<any[]>(() => {
    const groups: any[] = [
      {
        label: 'Home',
        items: [
          {
            label: 'Dashboard',
            icon: 'pi pi-fw pi-home',
            routerLink: ['/dashboard'],
          },
          {
            label: 'Zeltplan',
            icon: 'pi pi-fw pi-table',
            routerLink: ['/dashboard/zeltplan'],
          },
        ],
      },
      {
        label: 'Kundenverwaltung',
        items: [
          {
            label: 'Kunden',
            icon: 'pi pi-fw pi-id-card',
            routerLink: ['/dashboard/kunden'],
          },
          {
            label: 'Reservierungen',
            icon: 'pi pi-fw pi-shopping-cart',
            routerLink: ['/dashboard/reservierungen'],
          },
          {
            label: 'Rechnungen',
            icon: 'pi pi-fw pi-receipt',
            routerLink: ['/dashboard/rechnungen'],
          },
          {
            label: 'Anfragen',
            icon: 'pi pi-fw pi-comment',
            routerLink: ['/dashboard/anfragen'],
            badge: this.inquiriesStore.openInquiriesCount,
          },
          {
            label: 'Kunden Duplikate',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/dashboard/kunden-duplikate'],
          },
          {
            label: 'Bankabgleich',
            icon: 'pi pi-fw pi-building-columns',
            routerLink: ['/dashboard/bank-sync'],
          },
        ],
      },
      ...(this.canWrite()
        ? [
            {
              label: 'Export & Druck',
              items: [
                {
                  label: 'DHL Versandlisten',
                  icon: 'pi pi-fw pi-truck',
                  routerLink: ['/dashboard/versandlisten'],
                },
                {
                  label: 'Tischkarten',
                  icon: 'pi pi-fw pi-file-word',
                  routerLink: ['/dashboard/tischkarten'],
                },
                {
                  label: 'Exportlisten',
                  icon: 'pi pi-fw pi-file',
                  routerLink: ['/dashboard/exportlisten'],
                },
              ],
            },
          ]
        : []),
      {
        label: 'Auswertungen',
        items: [
          {
            label: 'Fest Produktauswertung',
            icon: 'pi pi-fw pi-chart-bar',
            routerLink: ['/dashboard/fest-produktauswertung'],
          },
          {
            label: 'Fest Auslastung',
            icon: 'pi pi-fw pi-chart-line',
            routerLink: ['/dashboard/festival-stats'],
          },
          {
            label: 'Fest Umsatz',
            icon: 'pi pi-fw pi-wallet',
            routerLink: ['/dashboard/festival-summary'],
          },
          {
            label: 'Fest Kundengruppen',
            icon: 'pi pi-fw pi-chart-pie',
            routerLink: ['/dashboard/fest-kundengruppen'],
          },
          {
            label: 'Fest Reservierungen',
            icon: 'pi pi-fw pi-calendar',
            routerLink: ['/dashboard/fest-reservierungen'],
          },
          {
            label: 'Top 100 Kunden',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/dashboard/top-100-kunden'],
          },
        ],
      },
      {
        label: 'Festverwaltung',
        items: [
          {
            label: 'Feste',
            icon: 'pi pi-fw pi-database',
            routerLink: ['/dashboard/feste'],
          },
          {
            label: 'Zeltplan Layout',
            icon: 'pi pi-fw pi-table',
            routerLink: ['/dashboard/bereichegrid'],
          },
          {
            label: 'Bereiche',
            icon: 'pi pi-fw pi-ticket',
            routerLink: ['/dashboard/bereiche'],
          },
          {
            label: 'Tage',
            icon: 'pi pi-fw pi-calendar',
            routerLink: ['/dashboard/tage'],
          },
          {
            label: 'Logbücher',
            icon: 'pi pi-fw pi-book',
            routerLink: ['/dashboard/log-buecher'],
          },
        ],
      },
      {
        label: 'Produktverwaltung',
        items: [
          {
            label: 'Produkte',
            icon: 'pi pi-fw pi-apple',
            routerLink: ['/dashboard/produkte'],
          },
          {
            label: 'Pakete',
            icon: 'pi pi-fw pi-th-large',
            routerLink: ['/dashboard/pakete'],
          },
          {
            label: 'Auftragsrabatt',
            icon: 'pi pi-fw pi-list-check',
            routerLink: ['/dashboard/auftragsrabatt'],
          },
          {
            label: 'Geldrabatte',
            icon: 'pi pi-fw pi-money-bill',
            routerLink: ['/dashboard/geldrabatte'],
          },
          {
            label: 'Codewörter',
            icon: 'pi pi-fw pi-tag',
            routerLink: ['/dashboard/codewoerter'],
          },
          {
            label: 'Versandoptionen',
            icon: 'pi pi-fw pi-truck',
            routerLink: ['/dashboard/versandoptionen'],
          },
        ],
      },
      {
        label: 'Vorlagen',
        items: [
          {
            label: 'E-Mails',
            icon: 'pi pi-fw pi-envelope',
            routerLink: ['/dashboard/email-templates'],
          },
        ],
      },
      {
        label: 'Einstellungen',
        items: [
          ...(this.canWrite()
            ? [
                {
                  label: 'Wartungsmodus',
                  icon: 'pi pi-fw pi-wrench',
                  routerLink: ['/dashboard/spa-wartung'],
                },
              ]
            : []),
          ...(this.isSuperAdmin()
            ? [
                {
                  label: 'Administratoren',
                  icon: 'pi pi-fw pi-user-plus',
                  routerLink: ['/dashboard/administratoren'],
                },
              ]
            : []),
        ],
      },
    ];

    return groups.filter((group) => group.items?.length);
  });
}
